import React, { useState } from 'react';
import { ApiService, DeviceMapping } from '../services/api';
import { googleDriveService } from '../services/googleDrive';

interface ArchiveDialogProps {
  mapping: DeviceMapping;
  scannedSerial: string;
  environment: string;
  apiService: ApiService;
  onClose: () => void;
  onArchiveComplete: () => void;
}

export const ArchiveDialog: React.FC<ArchiveDialogProps> = ({
  mapping,
  scannedSerial,
  environment,
  apiService,
  onClose,
  onArchiveComplete,
}) => {
  const [step, setStep] = useState<'confirm' | 'archiving' | 'success' | 'error'>('confirm');
  const [archiveProgress, setArchiveProgress] = useState<string>('');
  const [archivedFiles, setArchivedFiles] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleArchive = async () => {
    setStep('archiving');
    setArchiveProgress('Searching for files in Google Drive...');

    try {
      // Search and archive files in Google Drive
      const archiveResult = await googleDriveService.archiveSerialFiles(
        mapping.serialNumber,
        mapping.orientation as 'portrait' | 'landscape'
      );

      setArchivedFiles(archiveResult.archivedFiles);
      setArchiveProgress('Updating database...');

      // Update the database to mark etch as completed
      await apiService.updateMapping(
        mapping.serialNumber,
        mapping.imageCreated,
        true, // etchCompleted = true
        environment
      );

      setStep('success');

      // Notify parent component after a short delay
      setTimeout(() => {
        onArchiveComplete();
      }, 2000);
    } catch (error: any) {
      console.error('Archive failed:', error);
      setErrorMessage(error.message || 'Failed to archive files');
      setStep('error');
    }
  };

  const renderContent = () => {
    switch (step) {
      case 'confirm':
        return (
          <>
            <div style={dialogBodyStyle}>
              <div style={validationSectionStyle}>
                <h3 style={sectionTitleStyle}>📋 Validation Results</h3>

                <div style={validationItemStyle}>
                  <span style={checkIconStyle}>✅</span>
                  <div>
                    <strong>QR Code Detected</strong>
                    <div style={detailTextStyle}>{mapping.qrIdentifier}</div>
                  </div>
                </div>

                <div style={validationItemStyle}>
                  <span style={checkIconStyle}>✅</span>
                  <div>
                    <strong>Serial Number Matched</strong>
                    <div style={detailTextStyle}>
                      Scanned: {scannedSerial} | Database: {mapping.serialNumber}
                    </div>
                  </div>
                </div>

                <div style={validationItemStyle}>
                  <span style={infoIconStyle}>ℹ️</span>
                  <div>
                    <strong>Orientation</strong>
                    <div style={detailTextStyle}>{mapping.orientation}</div>
                  </div>
                </div>

                <div style={validationItemStyle}>
                  <span style={infoIconStyle}>ℹ️</span>
                  <div>
                    <strong>Current Status</strong>
                    <div style={detailTextStyle}>
                      Image Created: {mapping.imageCreated ? 'Yes' : 'No'} |
                      Etch Completed: {mapping.etchCompleted ? 'Yes' : 'No'}
                    </div>
                  </div>
                </div>
              </div>

              <div style={questionSectionStyle}>
                <h3 style={questionTitleStyle}>Has the etching been completed?</h3>
                <p style={questionTextStyle}>
                  If you confirm that etching is complete, the system will:
                </p>
                <ol style={actionListStyle}>
                  <li>Archive all files for serial {mapping.serialNumber} to the archive folder</li>
                  <li>Remove files from the {mapping.orientation} folder</li>
                  <li>Mark the device as etched in the database</li>
                </ol>
              </div>
            </div>

            <div style={dialogFooterStyle}>
              <button onClick={onClose} style={cancelButtonStyle}>
                Not Yet
              </button>
              <button onClick={handleArchive} style={confirmButtonStyle}>
                Yes, Archive Files
              </button>
            </div>
          </>
        );

      case 'archiving':
        return (
          <div style={dialogBodyStyle}>
            <div style={progressSectionStyle}>
              <div style={spinnerStyle}>⏳</div>
              <h3 style={progressTitleStyle}>Archiving in Progress</h3>
              <p style={progressTextStyle}>{archiveProgress}</p>
            </div>
          </div>
        );

      case 'success':
        return (
          <div style={dialogBodyStyle}>
            <div style={successSectionStyle}>
              <div style={successIconStyle}>✅</div>
              <h3 style={successTitleStyle}>Archive Complete!</h3>
              <p style={successTextStyle}>
                Successfully archived {archivedFiles.length} file(s) for serial {mapping.serialNumber}
              </p>
              {archivedFiles.length > 0 && (
                <div style={fileListStyle}>
                  <strong>Archived Files:</strong>
                  <ul style={fileListItemsStyle}>
                    {archivedFiles.map((file, index) => (
                      <li key={index}>{file}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );

      case 'error':
        return (
          <>
            <div style={dialogBodyStyle}>
              <div style={errorSectionStyle}>
                <div style={errorIconStyle}>❌</div>
                <h3 style={errorTitleStyle}>Archive Failed</h3>
                <p style={errorTextStyle}>{errorMessage}</p>
              </div>
            </div>
            <div style={dialogFooterStyle}>
              <button onClick={onClose} style={cancelButtonStyle}>
                Close
              </button>
              <button onClick={handleArchive} style={retryButtonStyle}>
                Retry
              </button>
            </div>
          </>
        );
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={dialogStyle}>
        <div style={dialogHeaderStyle}>
          <h2 style={dialogTitleStyle}>🗄️ Archive QR Code Files</h2>
          {step === 'confirm' && (
            <button onClick={onClose} style={closeButtonStyle}>✕</button>
          )}
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
};

const dialogStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
  maxWidth: '600px',
  width: '90%',
  maxHeight: '80vh',
  overflow: 'hidden',
};

const dialogHeaderStyle: React.CSSProperties = {
  padding: '20px 24px',
  borderBottom: '1px solid #eee',
  background: 'linear-gradient(135deg, #6600ff, #4d00c0)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const dialogTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '20px',
  fontWeight: '600',
  color: 'white',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'white',
  fontSize: '24px',
  cursor: 'pointer',
  padding: '0',
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const dialogBodyStyle: React.CSSProperties = {
  padding: '24px',
  maxHeight: 'calc(80vh - 140px)',
  overflowY: 'auto',
};

const validationSectionStyle: React.CSSProperties = {
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '20px',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 16px 0',
  fontSize: '16px',
  fontWeight: '600',
  color: '#333',
};

const validationItemStyle: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
  marginBottom: '12px',
  alignItems: 'flex-start',
};

const checkIconStyle: React.CSSProperties = {
  color: '#4CAF50',
  fontSize: '20px',
  flexShrink: 0,
};

const infoIconStyle: React.CSSProperties = {
  color: '#2196F3',
  fontSize: '20px',
  flexShrink: 0,
};

const detailTextStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#666',
  marginTop: '2px',
  fontFamily: 'monospace',
};

const questionSectionStyle: React.CSSProperties = {
  backgroundColor: '#fff3cd',
  borderRadius: '8px',
  padding: '20px',
  border: '1px solid #ffc107',
};

const questionTitleStyle: React.CSSProperties = {
  margin: '0 0 12px 0',
  fontSize: '18px',
  fontWeight: '600',
  color: '#333',
};

const questionTextStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#666',
  marginBottom: '12px',
};

const actionListStyle: React.CSSProperties = {
  margin: '0 0 0 20px',
  fontSize: '14px',
  color: '#666',
  lineHeight: '1.8',
};

const dialogFooterStyle: React.CSSProperties = {
  padding: '16px 24px',
  borderTop: '1px solid #eee',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  backgroundColor: '#6c757d',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  cursor: 'pointer',
};

const confirmButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  backgroundColor: '#28a745',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
};

const retryButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  backgroundColor: '#ffc107',
  color: '#212529',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
};

// Progress styles
const progressSectionStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '40px',
};

const spinnerStyle: React.CSSProperties = {
  fontSize: '48px',
  animation: 'spin 1s linear infinite',
  display: 'inline-block',
};

const progressTitleStyle: React.CSSProperties = {
  margin: '20px 0 10px',
  fontSize: '18px',
  fontWeight: '600',
  color: '#333',
};

const progressTextStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#666',
};

// Success styles
const successSectionStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '40px',
};

const successIconStyle: React.CSSProperties = {
  fontSize: '48px',
  color: '#4CAF50',
};

const successTitleStyle: React.CSSProperties = {
  margin: '20px 0 10px',
  fontSize: '18px',
  fontWeight: '600',
  color: '#333',
};

const successTextStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#666',
  marginBottom: '20px',
};

const fileListStyle: React.CSSProperties = {
  textAlign: 'left',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  padding: '15px',
  fontSize: '13px',
};

const fileListItemsStyle: React.CSSProperties = {
  margin: '10px 0 0 20px',
  color: '#666',
};

// Error styles
const errorSectionStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '40px',
};

const errorIconStyle: React.CSSProperties = {
  fontSize: '48px',
  color: '#dc3545',
};

const errorTitleStyle: React.CSSProperties = {
  margin: '20px 0 10px',
  fontSize: '18px',
  fontWeight: '600',
  color: '#333',
};

const errorTextStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#dc3545',
};